import { useTranslation } from "react-i18next";
import { Form, Icon, Image, Message, Popup } from "semantic-ui-react";

import { useSelectedLocation } from "components";
import { createFormHelper } from "lib/Form";
import "modules/KioskDashboard/components/CreateUpdateKioskModal/components/CreateUpdateKioskForm/CreateUpdateKioskForm.scss";
import { getFileFormData } from "utils/common";

import type { UseMutateAsyncFunction } from "@tanstack/react-query";
import type { CreateUpdateKioskPayload } from "modules/KioskDashboard/components/CreateUpdateKioskModal/hooks";

type KioskCreateUpdateFormProps = {
  hasErrors: boolean;
  confirmImageUrl: string;
  standByImageUrl: string;
  isUploadingStandByImagePending: boolean;
  isUploadingConfirmationImagePending: boolean;
  uploadStandByImg: UseMutateAsyncFunction<string, Error, FormData, unknown>;
  uploadConfirmationImg: UseMutateAsyncFunction<string, Error, FormData, unknown>;
};

const generateFormData = (file: File, locationId: string) => {
  const formData = getFileFormData(file);
  formData.append("dealer_location_id", locationId);
  return formData;
};

const handleExtractHrefFromCustomWebPageDisplayText = (customWebPageDisplayText?: string) => {
  if (!customWebPageDisplayText) return null;

  const html = new DOMParser().parseFromString(customWebPageDisplayText, "text/html");
  const anchor = html.querySelector("a[href]");

  if (anchor) return anchor.getAttribute("href");

  return null;
};

const FormHelper = createFormHelper<CreateUpdateKioskPayload>();

export const CreateUpdateKioskForm = ({
  hasErrors,
  confirmImageUrl,
  standByImageUrl,
  isUploadingStandByImagePending,
  isUploadingConfirmationImagePending,
  uploadStandByImg,
  uploadConfirmationImg
}: KioskCreateUpdateFormProps) => {
  const { t } = useTranslation();
  const { watch } = FormHelper.useFormContext();
  const [name, confirmation_title, is_custom_web_page_enabled, custom_web_page_display_text] = watch([
    "name",
    "confirmation_title",
    "is_custom_web_page_enabled",
    "custom_web_page_display_text"
  ]);

  const { selectedLocation } = useSelectedLocation();

  const customWebPageHref = handleExtractHrefFromCustomWebPageDisplayText(custom_web_page_display_text);
  const isCustomWebPageTextMissing = hasErrors && is_custom_web_page_enabled && !custom_web_page_display_text;

  const handleUploadConfirmImage = async (file: File) => {
    if (!selectedLocation) return;
    await uploadConfirmationImg(generateFormData(file, String(selectedLocation.id)));
  };

  const handleUploadStandByImage = async (file: File) => {
    if (!selectedLocation) return;
    await uploadStandByImg(generateFormData(file, String(selectedLocation.id)));
  };

  return (
    <div className="CreateUpdateKioskForm">
      <Form.Field required error={!name && hasErrors}>
        <label>{t("v8_title", "Title")} </label>
        <FormHelper.Field.Input
          name="name"
          inputProps={{
            placeholder: t("v8_title", "Title"),
            fluid: true,
            autoComplete: "off"
          }}
        />
      </Form.Field>

      <Form.Field required error={hasErrors && !confirmation_title}>
        <label>{t("v8_adjustable_text", "Adjustable Text")} </label>
        <FormHelper.Field.Input
          name="confirmation_title"
          inputProps={{
            placeholder: t("v8_adjustable_text", "Adjustable Text"),
            fluid: true,
            autoComplete: "off"
          }}
        />
      </Form.Field>

      <Form.Field>
        <label>{t("v8_confirmation_image", "Confirmation Image")} </label>
        <FormHelper.Field.FileDragDrop
          isUploading={isUploadingConfirmationImagePending}
          onChange={handleUploadConfirmImage}
          name="confirmation_image_url"
          id="confirmation-image-url"
          required={!confirmImageUrl}
        />
      </Form.Field>

      <div className="kiosk-create-form-image-container">
        <Image src={confirmImageUrl} alt="" />
      </div>

      <Form.Field>
        <label>
          {t("v8_standby_image", "Standby Image")}{" "}
          <Popup
            trigger={<Icon className="info circle" />}
            content={
              <div>
                <span>{`${t("v8_optimal_image_resolution", "Optimal image resolution")}:`}</span>
                <span> 1920 x 1080 </span>
              </div>
            }
          />
        </label>
        <FormHelper.Field.FileDragDrop
          onChange={handleUploadStandByImage}
          isUploading={isUploadingStandByImagePending}
          name="stand_by_image_url"
          id="stand-by-image-url"
        />
      </Form.Field>

      <Form.Field>
        <div className="kiosk-create-form-image-container">
          <Image src={standByImageUrl} alt="" />
        </div>
      </Form.Field>

      <FormHelper.Field.Checkbox
        name="is_custom_web_page_enabled"
        label={t("v8_active_kiosk_additional_screen", "Active Kiosk additional screen")}
        checkboxProps={{ toggle: true, className: "kiosk-create-form-checkbox" }}
      />

      {isCustomWebPageTextMissing && (
        <Message negative className="custom-webapage-error-msg">
          <h4>{t("v8_no_url_entered", "No URL Entered")}</h4>
        </Message>
      )}

      {is_custom_web_page_enabled && (
        <Form.Field>
          <FormHelper.Field.Quill
            name="custom_web_page_display_text"
            quillProps={{
              modules: { toolbar: ["bold", "italic", "underline", "link"] },
              value: custom_web_page_display_text,
              className: `KioskQuill ${isCustomWebPageTextMissing ? "QuillError" : ""}`
            }}
          />
        </Form.Field>
      )}

      {customWebPageHref && is_custom_web_page_enabled && (
        <div className="custom-web-page-preview-container">
          <div className="custom-web-page-preview-title">{t("v8_link_preview", "Link Preview")}</div>
          <iframe title={t("v8_custom_web_page_preview", "Custom Web Page Preview")} className="custom-web-page-preview" src={customWebPageHref} />
        </div>
      )}
    </div>
  );
};
